import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { workspaceService } from '../../services/workspaceService';
import Sidebar from '../../components/ui/Sidebar';
import ProjectSwitcher from '../../components/ui/ProjectSwitcher';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import Icon from '../../components/AppIcon';
import GeneralSettings from './components/GeneralSettings';
import DomainSettings from './components/DomainSettings';
import IntegrationSettings from './components/IntegrationSettings';
import TeamSettings from './components/TeamSettings';
import PerformanceSettings from './components/PerformanceSettings';
import DeploymentSettings from './components/DeploymentSettings';
import { AlertCircle } from 'lucide-react';

export default function ProjectSettings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [workspaceData, setWorkspaceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setError] = useState('');
  const { isConnected, queueStatus } = useConnectionStatus();

  // Load workspace data
  useEffect(() => {
    const loadWorkspace = async () => {
      if (!user?.id) return;
      
      try {
        const workspaces = await workspaceService?.getUserWorkspaces(user?.id);
        if (workspaces?.length > 0) {
          setWorkspaceData(workspaces?.[0]);
        }
      } catch (error) {
        console.error('Failed to load workspace:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWorkspace();
  }, [user?.id]);

  // Real-time subscription for workspace updates
  useRealtimeSubscription({
    table: 'workspaces',
    event: 'UPDATE',
    filter: workspaceData?.id ? { column: 'id', value: workspaceData?.id } : null,
    enabled: !!workspaceData?.id,
    onUpdate: (updatedWorkspace) => {
      setWorkspaceData(updatedWorkspace);
    }
  });

  // Real-time subscription for team member changes
  useRealtimeSubscription({
    table: 'workspace_members',
    event: '*',
    filter: workspaceData?.id ? { column: 'workspace_id', value: workspaceData?.id } : null,
    enabled: !!workspaceData?.id,
    onChange: (payload) => {
      console.log('Team member changed:', payload);
      // Refresh workspace data to get updated member count
      if (workspaceData?.id) {
        workspaceService?.getWorkspaceById(workspaceData?.id)?.then(updated => setWorkspaceData(updated))?.catch(console.error);
      }
    }
  });

  const handleProjectChange = (project) => {
    setCurrentProject(project);
  };

  const handleLogout = () => {
    // Logout logic handled by AuthContext
  };

  const handleSave = async () => {
    if (!workspaceData?.id) {
      setError('No workspace selected');
      return;
    }

    try {
      setSaving(true);
      await workspaceService.updateWorkspace(workspaceData.id, workspaceData);
      
      if (!isConnected) {
        setSuccessMessage('Settings saved locally and will sync when online');
      } else {
        setSuccessMessage('Settings saved successfully');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(error?.message);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'domain', label: 'Domain', icon: 'Globe' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
    { id: 'team', label: 'Team', icon: 'Users' },
    { id: 'performance', label: 'Performance', icon: 'Zap' },
    { id: 'deployment', label: 'Deployment', icon: 'Rocket' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettings />;
      case 'domain':
        return <DomainSettings />;
      case 'integrations':
        return <IntegrationSettings />;
      case 'team':
        return <TeamSettings />;
      case 'performance':
        return <PerformanceSettings />;
      case 'deployment':
        return <DeploymentSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        user={user}
        onLogout={handleLogout}
      />
      <div
        className={`transition-all duration-250 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <div className="flex flex-col h-screen">
          <header className="sticky top-0 z-30 bg-card border-b border-border">
            <div className="px-4 md:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="lg:hidden">
                    <ProjectSwitcher 
                      currentProject={currentProject}
                      onProjectChange={handleProjectChange}
                    />
                  </div>
                  <div>
                    <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground">
                      Project Settings
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                      Configure your project properties and deployment options
                    </p>
                  </div>
                </div>
                <div className="hidden lg:block">
                  <UserAccountMenu 
                    user={user}
                    onLogout={handleLogout}
                  />
                </div>
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col lg:flex-row">
              <aside className="lg:w-64 bg-card border-b lg:border-b-0 lg:border-r border-border overflow-x-auto lg:overflow-y-auto">
                <div className="p-4 lg:p-6">
                  <nav className="flex lg:flex-col gap-2 min-w-max lg:min-w-0">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-250 whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <Icon
                          name={tab?.icon}
                          size={20}
                          className={activeTab === tab?.id ? 'text-primary-foreground' : 'text-current'}
                        />
                        <span className="caption font-medium">{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              <main className="flex-1 overflow-y-auto">
                <div className="p-4 md:p-6 lg:p-8 max-w-5xl">
                  {renderTabContent()}
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>

      {/* Offline Indicator */}
      {!isConnected && (
        <div className="max-w-6xl mx-auto px-8 pt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                Working offline - Changes will sync when connection is restored
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}